import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useState,
  type ComponentPropsWithoutRef,
  type ReactNode,
} from 'react';

type PageHeaderPortalTargetRegistration = (target: HTMLElement) => () => void;

const PageHeaderPortalTargetContext = createContext<HTMLElement | null>(null);
const PageHeaderPortalRegistrationContext = createContext<PageHeaderPortalTargetRegistration | null>(null);

export type PageHeaderPortalProviderProps = {
  children: ReactNode;
};

// The provider keeps portal ownership with route-local PageHeader instances while
// allowing a shell to opt in by mounting one target.
export const PageHeaderPortalProvider = ({ children }: PageHeaderPortalProviderProps) => {
  const [targets, setTargets] = useState<HTMLElement[]>([]);
  const registerTarget = useCallback<PageHeaderPortalTargetRegistration>(target => {
    setTargets(currentTargets => [...currentTargets, target]);

    return () => {
      setTargets(currentTargets => currentTargets.filter(currentTarget => currentTarget !== target));
    };
  }, []);
  const target = targets.at(-1) ?? null;

  return (
    <PageHeaderPortalRegistrationContext.Provider value={registerTarget}>
      <PageHeaderPortalTargetContext.Provider value={target}>{children}</PageHeaderPortalTargetContext.Provider>
    </PageHeaderPortalRegistrationContext.Provider>
  );
};

export type PageHeaderPortalTargetProps = ComponentPropsWithoutRef<'div'>;

// A target without a provider remains a normal empty element. This makes it safe
// for isolated rendering while PageHeader itself retains its in-flow fallback.
export const PageHeaderPortalTarget = ({ children, ...props }: PageHeaderPortalTargetProps) => {
  const registerTarget = useContext(PageHeaderPortalRegistrationContext);
  const [target, setTarget] = useState<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    if (target === null || registerTarget === null) return undefined;

    return registerTarget(target);
  }, [registerTarget, target]);

  return (
    <div ref={setTarget} {...props}>
      {children}
    </div>
  );
};

export const usePageHeaderPortalTarget = (): HTMLElement | null => useContext(PageHeaderPortalTargetContext);
